/* eslint-disable @typescript-eslint/no-magic-numbers */
import { CookieLibrary } from '@/core/apis/browser_storage_api/cookie_lib/cookie_lib';
import { TimeUtils } from '@/core/event_logic/utils/time_utils';

export const MetaCookieLogic = {
  convertFbCookieTimestampToMilliseconds(cookieData: string): string {
    const cookieParts = cookieData.split('.');
    const TIMESTAMP_INDEX = 2;
    // add three zeros to existing timestamp in seconds
    if (
      cookieParts?.[TIMESTAMP_INDEX]?.length ==
      MetaCookieLogic.TIMESTAMP_IN_SEC_LENGTH
    )
      cookieParts[TIMESTAMP_INDEX] += '000';
    return cookieParts.join('.');
  },
  cookiesToUpdate: ['_fbp', '_fbc'],

  extractFbclidFromFbcCookie: (fbcCookie: string): string => {
    const fbcCookieParts = fbcCookie.split('.');
    return fbcCookieParts[3] || '';
  },

  generateFbcCookie: (fbclid: string, creationTime: number): string => {
    const version = 'fb';
    const subdomainIndex = 1;
    const finalTimestamp = creationTime - 500; // create cookie 500ms in the past
    return `${version}.${subdomainIndex}.${finalTimestamp}.${fbclid}`;
  },

  generateFbpCookie: (creationTime: number): string => {
    const version = 'fb';
    const subdomainIndex = 1;
    const randomNumber = Math.floor(Math.random() * 1_000_000_000);
    const finalTimestamp = creationTime - 500; // create cookie 500ms in the past
    return `${version}.${subdomainIndex}.${finalTimestamp}.${randomNumber}`;
  },

  getFbcCookie: (currentTimestampMs: number): string | null => {
    const fbc = CookieLibrary.get('_fbc');
    if (!fbc) return null;
    const fbcParts = fbc.split('.');
    if (fbcParts.length < 4 || !fbc.startsWith('fb.1.')) return null;
    const fbcTimestamp = Number(
      fbcParts[2]?.length == MetaCookieLogic.TIMESTAMP_IN_SEC_LENGTH
        ? `${fbcParts[2]}000`
        : fbcParts[2],
    );
    const timeSinceFbc = currentTimestampMs - fbcTimestamp;
    const FBC_EXPIRY_MS = 31 * 86_400 * 1000; // 31 days
    if (
      timeSinceFbc > FBC_EXPIRY_MS ||
      MetaCookieLogic.isFbclidInvalid(fbcParts[3] || '')
    ) {
      // invalid cookie is stored
      CookieLibrary.remove('_fbc');
      return null;
    }
    return fbc;
  },

  getFbclidFromURL(url: string): string | undefined {
    if (url == '') return;
    try {
      const { decodedUrl } = JSON.parse(`{"decodedUrl":"${url}"}`);
      const params = new URL(decodedUrl).searchParams;
      let fbclid = params.get('fbclid') || params.get('_fbclid') || '';
      if (fbclid == '') {
        // use regex as fallback
        const regex = new RegExp('[?&]fbclid=([^&#]+)');
        const regexMatchArr = regex.exec(decodedUrl);
        if (regexMatchArr?.[1]) {
          fbclid = regexMatchArr[1];
        }
      }
      return fbclid;
    } catch {
      // if referrer not possible skip
      return;
    }
  },

  getFbpCookie: (): string | null => CookieLibrary.get('_fbp'),

  initializeFbpIfNeeded: async (timestamp?: number): Promise<void> => {
    if (!CookieLibrary.get('_fbp')) {
      const creationTime =
        timestamp ?? (await TimeUtils.getCurrentEpochInMsSynced());
      const fbpValue = MetaCookieLogic.generateFbpCookie(creationTime);
      MetaCookieLogic.setFbpCookie(fbpValue);
    }
  },

  isFbclidInvalid(fbclid: string) {
    return fbclid.toLowerCase() == fbclid;
  },

  retrieveAndStoreFbclid: async (
    timestamp?: number,
    initialReferrer?: string,
  ): Promise<void> => {
    const urlParams = new URLSearchParams(window.location.search);
    const referrerFbclid = MetaCookieLogic.getFbclidFromURL(document.referrer);
    const irFbclid = MetaCookieLogic.getFbclidFromURL(initialReferrer || '');
    // Prio: Current URL -> Current Referrer -> Initial Referrer
    const potentialFbclids = [
      urlParams.get('fbclid'),
      urlParams.get('_fbclid'),
      referrerFbclid,
      irFbclid,
    ];

    const fbclid = potentialFbclids.find(
      fbclidVal => fbclidVal && !MetaCookieLogic.isFbclidInvalid(fbclidVal),
    );

    if (fbclid && !MetaCookieLogic.isFbclidInvalid(fbclid)) {
      const currentFbc = CookieLibrary.get('_fbc');
      if (!currentFbc?.includes(fbclid)) {
        const creationTime =
          timestamp ?? (await TimeUtils.getCurrentEpochInMsSynced());
        const newFbcValue = MetaCookieLogic.generateFbcCookie(
          fbclid,
          creationTime,
        );
        MetaCookieLogic.setFbcCookie(newFbcValue);
      }
    }
  },

  setFbcCookie: (value: string): void => {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 31);
    CookieLibrary.set('_fbc', value, { expires: expirationDate });
  },

  setFbpCookie: (value: string): void => {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 90);
    CookieLibrary.set('_fbp', value, { expires: expirationDate });
  },

  TIMESTAMP_IN_SEC_LENGTH: 10,

  updateCookies(
    cookies: Record<string, string>,
    iqpars: Record<string, string[]>,
    timestamp: number,
  ): Record<string, string> {
    const newCookies: Record<string, string> = { ...cookies };

    // add three zeros to end of timestamp if it is in seconds to convert it to milliseconds
    if (
      timestamp &&
      timestamp.toFixed(0).length === MetaCookieLogic.TIMESTAMP_IN_SEC_LENGTH
    )
      timestamp *= 1000;

    // remove decimals if any
    const creationTime = Number.parseInt(timestamp.toString());

    if (
      MetaCookieLogic.cookiesToUpdate?.includes('_fbp') &&
      cookies &&
      !cookies['_fbp']
    ) {
      const maybeFbpCookie = MetaCookieLogic.getFbpCookie();
      if (!maybeFbpCookie) {
        newCookies['_fbp'] = MetaCookieLogic.generateFbpCookie(creationTime);
        MetaCookieLogic.setFbpCookie(newCookies['_fbp']);
      }
    }

    // use last added fbclid
    const lastFbclid = iqpars?.['fbclid']?.at(-1);

    if (
      MetaCookieLogic.cookiesToUpdate?.includes('_fbc') &&
      cookies &&
      !cookies['_fbc'] &&
      lastFbclid &&
      !MetaCookieLogic.isFbclidInvalid(lastFbclid)
    ) {
      const maybeFbcCookie = MetaCookieLogic.getFbcCookie(timestamp);
      if (!maybeFbcCookie && lastFbclid) {
        newCookies['_fbc'] = MetaCookieLogic.generateFbcCookie(
          lastFbclid,
          creationTime,
        );
        MetaCookieLogic.setFbcCookie(newCookies['_fbc']);
      }
    }

    // check if current cookies are in correct format
    const fbp = MetaCookieLogic.getFbpCookie();
    if (fbp) {
      const validFbp =
        MetaCookieLogic.convertFbCookieTimestampToMilliseconds(fbp);
      if (fbp != validFbp) {
        newCookies['_fbp'] = validFbp;
        MetaCookieLogic.setFbpCookie(validFbp);
      }
    }

    const fbc = MetaCookieLogic.getFbcCookie(timestamp);
    if (fbc) {
      const validFbc =
        MetaCookieLogic.convertFbCookieTimestampToMilliseconds(fbc);
      if (fbc != validFbc) {
        newCookies['_fbc'] = validFbc;
        MetaCookieLogic.setFbcCookie(validFbc);
      }
    }

    // Add any other cookie updates specific to this destination bundle here

    // always set the new fbc and fbp cookies remove if invalid
    const finalFbp = MetaCookieLogic.getFbpCookie();
    const finalFbc = MetaCookieLogic.getFbcCookie(timestamp);

    if (finalFbc) newCookies['_fbc'] = finalFbc;
    else delete newCookies['_fbc'];

    if (finalFbp) newCookies['_fbp'] = finalFbp;
    else delete newCookies['_fbp'];

    return newCookies;
  },
};
